import * as React from "react";
import { Confirm } from "components/dialog";
import { $, media, merge } from "glamor";
import theme from "styles/theme";
import MenuItem from "material-ui/MenuItem";
import { formatMessage } from "lib/globalize-util";
import { List, Map } from "immutable";
import { PreviewPanelView } from "./preview-panel-view";
import { FormView } from "./form-view";
import { DATATEST } from "constants/integration";
import { IShopViewSettings } from "./types";
import { KINDS_TO_ALLOW, NONE } from "../manage-tile-view/constants";
import { ValueTypes } from "constants/product-collection";

const styles = merge(
    {
        minHeight: 353,
        display: "flex",
        height: "460px",
        width: "100%",
        margin: "20px 0 50px 0",
        justifyContent: "space-evenly",
    },
    $(" > div", {
        display: "flex",
        flexDirection: "column",
        width: "100%",
    }),
    $(" .helpText", {
        fontSize: "14px",
        color: theme.palette.darkGrey,
        marginBottom: "20px",
    }),
    media("(max-width: 768px)", {
        display: "block",
        height: "auto",
    })
);

interface IProps {
    onClose: () => void;
    onConfirm: (viewSettings: IShopViewSettings) => void;
    initialShopSettings?: IShopViewSettings;
    itemDisplayProperty?: List<Map<string, any>>;
}

export const ManageShopView: React.FunctionComponent<IProps> = ({
    onClose,
    onConfirm,
    itemDisplayProperty,
    initialShopSettings,
}) => {
    const [viewSettings, setSettings] = React.useState<IShopViewSettings>(
        initialShopSettings ?? ({} as IShopViewSettings)
    );

    const handleCheckboxChange = (event, status) => {
        setSettings({
            ...viewSettings,
            [event.target.value]: status,
        });
    };

    const handleLineChange = (key) => (_event, _index, status) => {
        setSettings({
            ...viewSettings,
            [key]: status === NONE ? "" : status,
        });
    };

    const handleConfirm = () => {
        onConfirm(viewSettings);
    };

    const handlePriceChange = (key) => (_event, _index, status) => {
        setSettings({
            ...viewSettings,
            [key]: status === NONE ? "" : status,
        });
    };
    const selectOptions = React.useMemo(() => {
        const options = itemDisplayProperty
            .filter((schemaKey) => KINDS_TO_ALLOW.includes(schemaKey.get("kind")))
            .map((schemaKey) => {
                const value = schemaKey.get("field_name");
                const label = schemaKey.get("display_name");
                return <MenuItem key={value} value={value} primaryText={label} />;
            })
            .toArray();

        options.unshift(<MenuItem key={NONE} value={NONE} primaryText={formatMessage("None")} />);

        return options;
    }, [itemDisplayProperty]);

    const priceOptions = React.useMemo(() => {
        const options = itemDisplayProperty
            .filter((schemaKey) => schemaKey.get("value_type") === ValueTypes.NUMBER)
            .map((schemaKey) => {
                const value = schemaKey.get("field_name");
                const label = schemaKey.get("display_name");
                return <MenuItem key={value} value={value} primaryText={label} />;
            })
            .toArray();

        options.unshift(<MenuItem key={NONE} value={NONE} primaryText={formatMessage("None")} />);

        return options;
    }, [itemDisplayProperty]);

    let hasAtLeastOneUpdate = false;

    if (!initialShopSettings && viewSettings) {
        hasAtLeastOneUpdate = true;
    }
    if (initialShopSettings) {
        hasAtLeastOneUpdate = Object.keys(viewSettings).some((key) => viewSettings[key] !== initialShopSettings[key]);
    }
    const getTileSettingsValue = React.useMemo(() => {
        return Object.values(viewSettings).reduce(
            (target: {}, settingItem: string) => ({
                ...target,
                [settingItem]: itemDisplayProperty
                    .find((schemaKey) => schemaKey.get("field_name") === settingItem)
                    ?.get("display_name"),
            }),
            {}
        );
    }, [viewSettings, itemDisplayProperty]);

    return (
        <Confirm
            disabledConfirm={!hasAtLeastOneUpdate}
            contentStyle={{ maxWidth: "700px" }}
            title={formatMessage("Configure Shop View")}
            onConfirm={handleConfirm}
            onCancel={onClose}
            confirmLabel={formatMessage("Apply")}
            cancelLabel={formatMessage("Cancel")}
            open={true}
        >
            <div {...styles} data-test={DATATEST.ASSORTMENTS.SHOP.SETTINGS.MODAL.BODY}>
                <PreviewPanelView settingsValue={getTileSettingsValue} settings={viewSettings} />
                <div>
                    <p className="helpText">
                        {formatMessage("Select the data points that you would like to display on each image tile")}:
                    </p>
                    <FormView
                        settings={viewSettings}
                        fieldOptions={selectOptions}
                        priceOptions={priceOptions}
                        onLineChange={handleLineChange}
                        onCheckboxChange={handleCheckboxChange}
                        onPriceChange={handlePriceChange}
                    />
                </div>
            </div>
        </Confirm>
    );
};
